(() => {
  'use strict';

  // Shopify can reload a section or evaluate this asset again in the theme editor.
  if (window.KingdomDescription) {
    window.KingdomDescription.initialize(document);
    return;
  }

  const selector = '[data-watch-description]';
  const initialized = new WeakSet();
  const sectionLabels = new Set([
    'model background', 'about this reference', 'key features',
    'why this edition is special', 'specifications', 'condition report',
    'delivery contents', 'service & authenticity', 'closing'
  ]);
  const protectedAncestor = 'table,ul,ol,dl,pre,code,blockquote';
  const meaningfulContent = 'a,img,picture,video,audio,iframe,svg,canvas,form,input,button,select,textarea,table,ul,ol,pre,code,[id],[name]';
  const normalizeText = (value) => value.replace(/\s+/gu, ' ').trim();
  const isBreak = (node) => node.nodeType === 1 && node.tagName === 'BR';
  const isWhitespace = (node) => {
    if (node.nodeType === 3) return !normalizeText(node.textContent);
    // Some imported descriptions use a bold zero-width/BOM spacer between breaks.
    return node.nodeType === 1 && /^(SPAN|STRONG|B|EM|I)$/u.test(node.tagName)
      && !normalizeText(node.textContent) && !node.querySelector(meaningfulContent)
      && !node.hasAttribute('id') && !node.hasAttribute('name');
  };

  function splitParagraph(paragraph) {
    const nodes = Array.from(paragraph.childNodes);
    if (!nodes.length) return;
    const fragment = paragraph.ownerDocument.createDocumentFragment();
    let current = paragraph.cloneNode(false);
    let split = false;
    let hasParagraph = false;
    const appendParagraph = () => {
      if (current.hasChildNodes()) {
        fragment.appendChild(current);
        hasParagraph = true;
      }
      current = paragraph.cloneNode(false);
      // Only the first resulting paragraph may retain the original anchor.
      if (hasParagraph) current.removeAttribute('id');
    };

    for (let index = 0; index < nodes.length; index += 1) {
      if (!isBreak(nodes[index])) {
        current.appendChild(nodes[index]);
        continue;
      }
      let end = index;
      let breaks = 0;
      while (end < nodes.length && (isBreak(nodes[end]) || isWhitespace(nodes[end]))) {
        if (isBreak(nodes[end])) breaks += 1;
        end += 1;
      }
      if (breaks < 2) {
        current.appendChild(nodes[index]);
        continue;
      }
      split = true;
      appendParagraph();
      // Keep every original whitespace character, including nonbreaking spaces.
      // Break elements themselves contribute no text and are replaced by blocks.
      for (let spacer = index; spacer < end; spacer += 1) {
        if (!isBreak(nodes[spacer])) fragment.appendChild(nodes[spacer]);
      }
      index = end - 1;
    }
    appendParagraph();
    if (split) paragraph.replaceWith(fragment);
    else paragraph.replaceChildren(...fragment.firstChild.childNodes);
  }

  function promoteHeading(paragraph) {
    const label = normalizeText(paragraph.textContent).toLowerCase();
    if (!sectionLabels.has(label)) return;
    const substantive = Array.from(paragraph.childNodes).filter((node) => !isWhitespace(node));
    if (substantive.length !== 1 || substantive[0].nodeType !== 1
      || !/^(STRONG|B)$/u.test(substantive[0].tagName)
      || substantive[0].querySelector('a,br,img,video,audio')) return;
    const heading = paragraph.ownerDocument.createElement('h3');
    for (const attribute of paragraph.attributes) heading.setAttribute(attribute.name, attribute.value);
    heading.classList.add('kw-description-subheading');
    heading.append(...paragraph.childNodes);
    paragraph.replaceWith(heading);
  }

  function normalizeDescription(element) {
    if (initialized.has(element)) return false;
    const originalText = element.textContent;
    const candidate = element.cloneNode(true);
    // WatchTrack descriptions can contain spacer headings after real headings.
    // Retain their exact whitespace while removing empty accessibility headings.
    for (const heading of candidate.querySelectorAll('h1,h2,h3,h4,h5,h6')) {
      if (!normalizeText(heading.textContent) && !heading.querySelector(meaningfulContent)
        && !heading.hasAttribute('id') && !heading.hasAttribute('name')) {
        heading.replaceWith(heading.ownerDocument.createTextNode(heading.textContent));
      }
    }
    for (const paragraph of candidate.querySelectorAll('p')) {
      if (paragraph.closest(protectedAncestor)) continue;
      if (!normalizeText(paragraph.textContent) && !paragraph.querySelector(meaningfulContent)
        && !paragraph.hasAttribute('id') && !paragraph.hasAttribute('name')) {
        paragraph.replaceWith(paragraph.ownerDocument.createTextNode(paragraph.textContent));
      } else {
        splitParagraph(paragraph);
      }
    }
    for (const paragraph of candidate.querySelectorAll('p')) {
      if (!paragraph.closest(protectedAncestor)) promoteHeading(paragraph);
    }
    // Formatting must never alter a single character of merchant-authored copy.
    if (candidate.textContent !== originalText) return false;
    if (candidate.innerHTML !== element.innerHTML) element.replaceChildren(...candidate.childNodes);
    initialized.add(element);
    return true;
  }

  function initialize(root) {
    if (root.matches && root.matches(selector)) normalizeDescription(root);
    for (const element of root.querySelectorAll(selector)) normalizeDescription(element);
  }

  window.KingdomDescription = Object.freeze({ initialize, normalize: normalizeDescription });
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }
})();
